import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Controller, useForm, useWatch } from "react-hook-form";
import dayjs from "dayjs";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ReactComponent as Edit } from "../../../../../../assets/AccountingModule/uil_edit.svg";
import Modal from "../../../../../../components/Modal";
import styles from "./style";
import { computationTypeOptions, depreciationFrequencyOptions, assetTypeOptions, assetAccount, accumulatedDepreciationAccount, depreciationMethodOptions, assetLifeCounterTypeOptions } from "../../../../../../constants/data";
import { AssetFormValidate } from "../../../../../../helper/Validation";

const assets2Data = [
  {
    "_id": "1",
    "depreciationDate": "2025-08-01",
    "depreciationValue": 5000,
    "cumulatedDepreciationValue": 15000,
    "amountInBank": 25000
  },
  {
    "_id": "2",
    "depreciationDate": "2025-07-15",
    "depreciationValue": 3000,
    "cumulatedDepreciationValue": 12000,
    "amountInBank": 18000
  },
  {
    "_id": "3",
    "depreciationDate": "2025-06-10",
    "depreciationValue": 2000,
    "cumulatedDepreciationValue": 9000,
    "amountInBank": 15000
  },
  {
    "_id": "4",
    "depreciationDate": "2025-05-20",
    "depreciationValue": 1000,
    "cumulatedDepreciationValue": 7000,
    "amountInBank": 12000
  }
];

const AssetsViewPage = ({ singleAssetData, handleDelete, depreciationForcastList }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [open, setOpen] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      assetName: "",
      assetNo: "",
      serialNumber: "",
      purchaseValue: "",
      purchaseDate: null,
      currentValue: "",
      disposalValue: "",
      assetType: "",
      warrantyExpirationDate: null,
      description: "",
      depreciationMethod: "",
      computationType: "",
      depreciationFrequency: "",
      depreciationStartDate: null,
      assetLifeCounter: "",
      assetLifeCounterType: "",
      assetAccount: "",
      accumulatedDepreciationAccount: "",
      depreciationExpenseAccount: "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (singleAssetData && Object.keys(singleAssetData).length > 0) {
      reset({
        ...singleAssetData,
        purchaseDate: singleAssetData.purchaseDate ? dayjs(singleAssetData.purchaseDate) : null,
        warrantyExpirationDate: singleAssetData.warrantyExpirationDate ? dayjs(singleAssetData.warrantyExpirationDate) : null,
        depreciationStartDate: singleAssetData.depreciationStartDate ? dayjs(singleAssetData.depreciationStartDate) : null,
      });
    }
  }, [singleAssetData, reset]);

  const depreciationMethod = useWatch({ control, name: "depreciationMethod" });

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleView = () => {
    setIsEditMode(false);
    reset({
      ...singleAssetData,
      purchaseDate: singleAssetData.purchaseDate ? dayjs(singleAssetData.purchaseDate) : null,
      warrantyExpirationDate: singleAssetData.warrantyExpirationDate ? dayjs(singleAssetData.warrantyExpirationDate) : null,
      depreciationStartDate: singleAssetData.depreciationStartDate ? dayjs(singleAssetData.depreciationStartDate) : null,
    });
  };

  const handleSave = (data) => {
    setIsEditMode(false);
  };

  const handleDeleteItem = () => {
    setOpen(false);
    handleDelete(singleAssetData._id);
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleSave)}>
        <Box px="20px" display="flex" justifyContent={{ xs: "start", md: "space-between" }} alignItems={{ xs: "start", md: "center" }} mt={{ xs: 2, md: 2 }}>
          <Box sx={styles.header}>
            <Typography sx={styles.title}>
              {isEditMode ? "Edit Assets" : "View Assets"}
            </Typography>
          </Box>
          {isEditMode ? (
            <Box sx={styles.actionButtons}>
              <Button type="submit" variant="contained" sx={styles.saveButton}>
                Save
              </Button>
              <Button variant="outlined" sx={styles.cancelButton} onClick={handleView}>
                Cancel
              </Button>
            </Box>
          ) : (
            <Box sx={styles.editIcon}>
              <Edit onClick={handleEdit} sx={{ cursor: "pointer" }} />
              <Box sx={styles.dltIcon}>
                <DeleteOutlineIcon onClick={() => setOpen(true)} />
              </Box>
            </Box>
          )}
        </Box>
        <Box sx={styles.container}>
          <Box sx={styles.formSection}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Asset Name</Typography>
                  <Controller
                    name="assetName"
                    control={control}
                    rules={{ required: "Asset Name is required" }}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <OutlinedInput
                          {...field}
                          fullWidth
                          size="small"
                          sx={{
                            ...styles.textField,
                            ...(error && styles.errorField)
                          }}
                          placeholder="Enter Asset Name"
                          disabled={!isEditMode}
                          error={!!error}
                        />
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Asset #</Typography>
                  <Controller
                    name="assetNo"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <OutlinedInput
                          {...field}
                          fullWidth
                          size="small"
                          sx={styles.textField}
                          placeholder="Asset Number"
                          disabled={!isEditMode}
                        />
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Purchase Value</Typography>
                  <Controller
                    name="purchaseValue"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <OutlinedInput
                          {...field}
                          fullWidth
                          size="small"
                          sx={styles.textField}
                          placeholder="Purchase Value"
                          disabled={!isEditMode}
                        />
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Purchase Date</Typography>
                  <Controller
                    name="purchaseDate"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            {...field}
                            format="DD/MM/YYYY"
                            disabled={!isEditMode}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                size="small"
                                placeholder="DD/MM/YYYY"
                                sx={styles.dateTextField}
                                disabled={!isEditMode}
                              />
                            )}
                          />
                        </LocalizationProvider>
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Serial Number</Typography>
                  <Controller
                    name="serialNumber"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <OutlinedInput
                          {...field}
                          fullWidth
                          size="small"
                          sx={styles.textField}
                          placeholder="Serial Number"
                          disabled={!isEditMode}
                        />
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Current Value</Typography>
                  <Controller
                    name="currentValue"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <OutlinedInput
                          {...field}
                          fullWidth
                          size="small"
                          sx={styles.textField}
                          placeholder="Current Value"
                          disabled={!isEditMode}
                        />
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Disposal Value</Typography>
                  <Controller
                    name="disposalValue"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <OutlinedInput
                          {...field}
                          fullWidth
                          size="small"
                          sx={styles.textField}
                          placeholder="Disposal Value"
                          disabled={!isEditMode}
                        />
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Asset Type</Typography>
                  <Controller
                    name="assetType"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Select
                          {...field}
                          fullWidth
                          displayEmpty
                          size="small"
                          sx={{
                            ...styles.textField,
                            ...(error && styles.errorField)
                          }}
                          disabled={!isEditMode}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <MenuItem value="">
                            <span style={{ color: "#999" }}>Select Asset Type</span>
                          </MenuItem>
                          {assetTypeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Warranty Expiration Date</Typography>
                  <Controller
                    name="warrantyExpirationDate"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            {...field}
                            format="DD/MM/YYYY"
                            disabled={!isEditMode}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                size="small"
                                sx={styles.dateTextField}
                                placeholder="DD/MM/YYYY"
                                disabled={!isEditMode}
                              />
                            )}
                          />
                        </LocalizationProvider>
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={12}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Description</Typography>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <OutlinedInput
                          {...field}
                          fullWidth
                          multiline
                          minRows={2}
                          sx={styles.multilineTextField}
                          placeholder="Enter Description"
                          disabled={!isEditMode}
                        />
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box sx={styles.itemSectionDivide}>
          <Box sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2 }}></Box>
          <Typography sx={styles.gridTextDivide}>
            Depreciation Details
          </Typography>
        </Box>
        <Box sx={styles.container}>
          <Box sx={styles.formSection}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Depreciation Method</Typography>
                  <Controller
                    name="depreciationMethod"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Select
                          {...field}
                          fullWidth
                          displayEmpty
                          size="small"
                          sx={{
                            ...styles.textField,
                            ...(error && styles.errorField)
                          }}
                          disabled={!isEditMode}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <MenuItem value="">
                            <span style={{ color: "#999" }}>Select Depreciation Method</span>
                          </MenuItem>
                          {depreciationMethodOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>
              {depreciationMethod === "Declining Balance" && (
                <Grid item xs={12} md={4}>
                  <Box sx={styles.field}>
                    <Typography sx={styles.label}>Depreciation Percentage</Typography>
                    <Controller
                      name="depreciationPercentage"
                      control={control}
                      rules={AssetFormValidate.depreciationPercentage}
                      render={({ field, fieldState: { error } }) => (
                        <>
                          <OutlinedInput
                            {...field}
                            fullWidth
                            size="small"
                            sx={{
                              ...styles.textField,
                              ...(error && styles.errorField)
                            }}
                            error={!!error}
                            placeholder="Enter Depreciation Percentage"
                          />
                          {error && (
                            <Typography sx={styles.errorText}>
                              {error.message}
                            </Typography>
                          )}
                        </>
                      )}
                    />
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Computation Type</Typography>
                  <Controller
                    name="computationType"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Select
                          {...field}
                          fullWidth
                          displayEmpty
                          size="small"
                          sx={{
                            ...styles.textField,
                            ...(error && styles.errorField)
                          }}
                          disabled={!isEditMode}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <MenuItem value="">
                            <span style={{ color: "#999" }}>Select Computation Type</span>
                          </MenuItem>
                          {computationTypeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Depreciation Frequency</Typography>
                  <Controller
                    name="depreciationFrequency"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Select
                          {...field}
                          fullWidth
                          displayEmpty
                          size="small"
                          sx={{
                            ...styles.textField,
                            ...(error && styles.errorField)
                          }}
                          disabled={!isEditMode}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <MenuItem value="">
                            <span style={{ color: "#999" }}>Select Depreciation Frequency</span>
                          </MenuItem>
                          {depreciationFrequencyOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Depreciation Start Date</Typography>
                  <Controller
                    name="depreciationStartDate"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            {...field}
                            format="DD/MM/YYYY"
                            disabled={!isEditMode}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                size="small"
                                sx={styles.dateTextField}
                                placeholder="DD/MM/YYYY"
                                disabled={!isEditMode}
                              />
                            )}
                          />
                        </LocalizationProvider>
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Asset Life</Typography>
                  <Controller
                    name="assetLifeCounter"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <OutlinedInput
                          {...field}
                          fullWidth
                          size="small"
                          sx={styles.textField}
                          placeholder="Enter Asset Life"
                          disabled={!isEditMode}
                        />
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Asset Life Type</Typography>
                  <Controller
                    name="assetLifeCounterType"
                    control={control}
                    rules={AssetFormValidate.assetLifeType}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Select
                          {...field}
                          fullWidth
                          displayEmpty
                          size="small"
                          sx={{
                            ...styles.textField,
                            ...(error && styles.errorField)
                          }}
                          disabled={!isEditMode}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <MenuItem value="">
                            <span style={{ color: "#999" }}>Select Asset Life Type</span>
                          </MenuItem>
                          {assetLifeCounterTypeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box sx={styles.itemSectionDivide}>
          <Box sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2 }}></Box>
          <Typography sx={styles.gridTextDivide}>
            Account Details
          </Typography>
        </Box>
        <Box sx={styles.container}>
          <Box sx={styles.formSection}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Asset Account</Typography>
                  <Controller
                    name="assetAccount"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Select
                          {...field}
                          fullWidth
                          displayEmpty
                          size="small"
                          sx={{
                            ...styles.textField,
                            ...(error && styles.errorField)
                          }}
                          disabled={!isEditMode}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <MenuItem value="">
                            <span style={{ color: "#999" }}>Select Asset Account</span>
                          </MenuItem>
                          {assetAccount.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Accumulated Depreciation Account</Typography>
                  <Controller
                    name="accumulatedDepreciationAccount"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Select
                          {...field}
                          fullWidth
                          displayEmpty
                          size="small"
                          sx={{
                            ...styles.textField,
                            ...(error && styles.errorField)
                          }}
                          disabled={!isEditMode}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <MenuItem value="">
                            <span style={{ color: "#999" }}>Select Depreciation Account</span>
                          </MenuItem>
                          {accumulatedDepreciationAccount.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Depreciation Expense Account</Typography>
                  <Controller
                    name="depreciationExpenseAccount"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Select
                          {...field}
                          fullWidth
                          displayEmpty
                          size="small"
                          sx={{
                            ...styles.textField,
                            ...(error && styles.errorField)
                          }}
                          disabled={!isEditMode}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <MenuItem value="">
                            <span style={{ color: "#999" }}>Select Depreciation Account</span>
                          </MenuItem>
                          {depreciationFrequencyOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
        {!isEditMode && (
          <Box sx={styles.tableContainer}>
            <Box>
              <Typography sx={styles.tableTitle}>Depreciation Forecast</Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...styles.tableCell, color: '#878787' }}>DEPRECIATION DATE</TableCell>
                    <TableCell sx={{ ...styles.tableCell, color: '#878787' }}>DEPRECIATION VALUE</TableCell>
                    <TableCell sx={{ ...styles.tableCell, color: '#878787' }}>CUMULATED DEPRECIATION VALUE</TableCell>
                    <TableCell sx={{ ...styles.tableCell, color: '#878787' }}>AMOUNT IN BANK</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assets2Data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="h6">No Data</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    assets2Data.map((row) => (
                      <TableRow key={row._id}>
                        <TableCell sx={{ ...styles.tableCells, borderBottom: "none" }}>
                          {row?.depreciationDate || "N/A"}
                        </TableCell>
                        <TableCell sx={{ ...styles.tableCells, borderBottom: "none" }}>
                          {row?.depreciationValue || "N/A"}
                        </TableCell>
                        <TableCell sx={{ ...styles.tableCells, borderBottom: "none" }}>
                          {row?.cumulatedDepreciationValue || "N/A"}
                        </TableCell>
                        <TableCell sx={{ ...styles.tableCells, borderBottom: "none" }}>
                          {row?.amountInBank || "N/A"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
        <Modal open={open} setOpen={setOpen} deletetText={"Delete"} cancelText={"Cancel"} title={"Delete Customers"} handleDelete={handleDeleteItem}>
          <Box>
            <Typography>Are you sure you want to Delete?</Typography>
          </Box>
        </Modal>
      </form>
    </>
  );
};

export default AssetsViewPage;